import {
  AnimatedContentLoader,
  DoubleColumnLayout,
  DoubleColumnLayoutColumn,
  SingleColumnLayout,
  UploadDropzone
} from '@components'
import { Spinner } from '@components/ui/Primitives'
import { useBusinessContext, useDateRangeContext } from '@context'
import { useDraftFilterFromQuery, useLocalStorage, useQueryParam } from '@hooks'
import {
  collectFromPages,
  convertToJSON,
  fetchDocuments,
  IDocument,
  UploadCancelledError,
  UploadOptions
} from '@query'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import styled from 'styled-components'
import { DocumentActionBar } from './DocumentActionBar'
import { DocumentDetails } from './DocumentDetails'
import { DocumentImportTable } from './DocumentImport/DocumentImportTable'
import { DocumentList } from './DocumentList'
import { DocumentPageOnboarding } from './DocumentPageOnboarding'
import { FirstTransactionFetchNoticeBanner } from './FirstTransactionFetchNotice'
import { IFilter } from '@root/components/ActionBar/FilterBar/FilterSelection'
import { fileToBase64 } from '@utils/fileToBase64.ts'
import PlanRecommendationBanner from './PlanRecommendationBanner'

interface DocumentPageProps {
  location: string
}

type JSONObject = { [key: string]: any }

export const DocumentPage: React.FC<DocumentPageProps> = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const [search, setSearch] = useState('')

  const [filters, setFilter] = useLocalStorage<IFilter[]>(`${slug}.filters.document`, [])
  const [documentId, setDocumentId] = useQueryParam('id', value => parseInt(value, 10))
  const [sourceJSON, setSourceJSON] = useState<JSONObject[]>()

  const { isActive, selectedRange } = useDateRangeContext()

  useDraftFilterFromQuery(filters, setFilter)

  const handleSourceUpload = async (file: File, { signal }: UploadOptions = {}) => {
    const contents = await fileToBase64(file)
    const fileBase = contents.split(',')[1]
    const fileJSON = await convertToJSON({
      format: 'csv',
      base64: fileBase
    })
    if (signal?.aborted) throw new UploadCancelledError()

    setSourceJSON(fileJSON)
  }

  const combinedFilters = useMemo(
    () => ({
      search,
      ...filters?.reduce(
        (prev, current) => ({
          ...prev,
          ...current
        }),
        {}
      ),
      ...(isActive
        ? {
            date_from: selectedRange?.date_from,
            date_to: selectedRange?.date_to
          }
        : {})
    }),
    [search, filters, selectedRange]
  )

  const { data, isLoading, isPlaceholderData, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [slug, 'documents', combinedFilters],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) =>
      fetchDocuments(
        { slug },
        {
          page: pageParam,
          view: 'list',
          ...combinedFilters
        }
      ),
    getNextPageParam: lastPage => lastPage.next,
    getPreviousPageParam: lastPage => lastPage.prev,
    placeholderData: keepPreviousData
  })
  const documents = collectFromPages<IDocument>(data)

  const onClose = () => setDocumentId(undefined)
  const isEmpty = documents.length === 0 && !isLoading

  const noDatefilters = !isActive

  const hasNoDocuments = isEmpty && filters?.length === 0 && !search && noDatefilters

  if (sourceJSON) {
    return (
      <SingleColumnLayout header={t('document.documentImportTitle')}>
        <DocumentImportTable data={sourceJSON} onClose={() => setSourceJSON(undefined)} />
      </SingleColumnLayout>
    )
  }

  return (
    <DoubleColumnLayout
      header={t('document.mainTitle')}
      isRightVisible={Boolean(documentId)}
      onRightClose={onClose}
    >
      <DoubleColumnLayoutColumn innerKey="column-left">
        <UploadDropzone fileTypes={['.csv']} uploadFile={handleSourceUpload}>
          <DocumentPageWrapper>
            <FirstTransactionFetchNoticeBanner />
            <DocumentActionBar
              documents={documents}
              onSearch={search => setSearch(search)}
              onFilter={filters => setFilter(filters)}
              uploadFile={handleSourceUpload}
              filters={filters}
            />
            <PlanRecommendationBanner />
            <DocumentListWrapper>
              <DocumentListBody $stale={isPlaceholderData}>
                <AnimatedContentLoader
                  isLoading={isLoading}
                  isEmpty={isEmpty}
                  isEmptyContent={hasNoDocuments && <DocumentPageOnboarding />}
                  isEmptyDescription={t('document.listEmptySearch')}
                >
                  <DocumentList
                    documents={documents}
                    onScrollBottom={async () => {
                      if (hasNextPage) {
                        await fetchNextPage()
                      }
                    }}
                  />
                </AnimatedContentLoader>
              </DocumentListBody>
              {isPlaceholderData ? (
                <StaleSpinner>
                  <Spinner size={22} />
                </StaleSpinner>
              ) : null}
            </DocumentListWrapper>
          </DocumentPageWrapper>
        </UploadDropzone>
      </DoubleColumnLayoutColumn>

      <DoubleColumnLayoutColumn innerKey={`document-${documentId}`} isRight={true}>
        {documentId && <DocumentDetails documentId={documentId} onClose={onClose} />}
      </DoubleColumnLayoutColumn>
    </DoubleColumnLayout>
  )
}

const DocumentPageWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const DocumentListWrapper = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`

const DocumentListBody = styled.div<{ $stale?: boolean }>`
  height: 100%;
  opacity: ${({ $stale }) => ($stale ? 0.3 : 1)};
  transition: opacity 0.15s;
`

const StaleSpinner = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: ${({ theme }) => theme.spacing.xl}rem;
  pointer-events: none;
`
